/* eslint-disable @typescript-eslint/no-explicit-any */
import type { RestConnectorConfig } from "oi-ecp-data-connector";
import { RestDataConnector } from "oi-ecp-data-connector";


/**
 * Shared base connector functionality:
 * - enforces consistent error handling (throw on non-2xx)
 * - provides a helper for Authorization header injection
 *
 * Domain connectors (clients/templates) should extend this.
 */
export class DataConnectorBase extends RestDataConnector {
  constructor(cfg: RestConnectorConfig) {
    super(cfg);
  }

  protected setEndpointAuthHeader(endpoint: string, token?: string) {
    const cfg: any = (this as any).config;
    if (!cfg?.restApiConfig?.[endpoint]) return;

    const current = cfg.restApiConfig?.[endpoint]?.headers || {};
    if (token) {
      cfg.restApiConfig[endpoint].headers = {
        ...current,
        Authorization: `Bearer ${token}`,
      };
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { Authorization: _, ...rest } = current;
    cfg.restApiConfig[endpoint].headers = rest;
  }

  protected async restCallStrict<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, unknown>
  ): Promise<T> {
    const result: any = await this.restCall(endpoint as any, body as any, params as any);

    // Network failures (e.g. offline) often come back as an object without statusCode.
    // Ensure those are treated as failures instead of being stored as success data.
    // Example: { error: {}, additionalContext: { error: {} }, where: ... }
    const hasErrorObject =
      result?.error != null &&
      typeof result.error === "object" &&
      Object.keys(result.error).length > 0;
    const hasNestedErrorObject =
      result?.additionalContext?.error != null &&
      typeof result.additionalContext.error === "object" &&
      Object.keys(result.additionalContext.error).length > 0;
    if (
      (result && (result.where || result.additionalContext)) &&
      (hasErrorObject || hasNestedErrorObject)
    ) {
      // Prefer common shapes if present
      const message =
        typeof result?.message === "string"
          ? result.message
          : typeof result?.error?.message === "string"
            ? result.error.message
            : typeof result?.additionalContext?.error?.message === "string"
              ? result.additionalContext.error.message
              : "Request failed (network error)";
      throw new Error(message);
    }

    // Some connector implementations resolve (instead of throw) on 4xx/5xx and include
    // statusCode/url in the payload.
    const statusCode: number | undefined =
      typeof result?.statusCode === "number" ? result.statusCode : undefined;

    if (statusCode !== undefined && (statusCode < 200 || statusCode >= 300)) {
      const message =
        typeof result?.data?.error === "string"
          ? result.data.error
          : typeof result?.error === "string"
            ? result.error
            : `Request failed with status ${statusCode}`;
      throw new Error(message);
    }

    // Extra safety if API returns 200 with an embedded error.
    if (typeof result?.error === "string") {
      throw new Error(result.error);
    }
    if (typeof result?.data?.error === "string") {
      throw new Error(result.data.error);
    }

    // Some integrations use an object for `error` instead of a string.
    if (result?.error && typeof result.error === "object") {
      const message = (result.error as any)?.message;
      throw new Error(typeof message === "string" ? message : "Request failed");
    }

    // Many endpoints return a standard envelope: { data: <payload>, statusCode, url }
    // For app code we want the payload, not the envelope.
    if (result && typeof result === "object" && "data" in result) {
      return (result as any).data as T;
    }

    return result as T;
  }
}
